import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { platformMeta, platformSolidFill } from '../../promote/platformMeta';
import { SocialPostThumb } from './SocialPostThumb';
import { postPermalink } from '../../promote/socialNumbers';
import { bestPostNumbers, postTypeMessages } from './socialSummary';
import { SocialSummaryPost } from './socialSummaryTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Social block; each part uses its own slice of it */
import styles from './SocialBlock.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialBestPostCardProps {
  post: SocialSummaryPost;
}

/**
 * The best post (design d1): thumbnail, channel, type badge, date, the caption on two lines at
 * most, then views, likes and comments. The card is a link to the post on its platform when the
 * platform gave an address.
 */
const SocialBestPostCardComponent: React.FC<SocialBestPostCardProps> = ({ post }) => {
  const intl = useIntl();
  const meta = platformMeta[post.platform];
  const numbers = useMemo(() => bestPostNumbers(post), [post]);
  const permalink = postPermalink(post);

  const body = (
    <>
      <SocialPostThumb url={post.thumbnailUrl} />
      <div className={styles.bestText}>
        <div className={styles.bestMeta}>
          <span
            className={clsx(styles.mark, styles.markSmall)}
            style={{ background: platformSolidFill(post.platform) }}
          >
            <FontAwesomeIcon icon={meta.icon} />
          </span>
          {meta.name}
          <span className={styles.typeBadge}>{intl.formatMessage(postTypeMessages[post.type])}</span>
          <span>{intl.formatDate(post.publishedAt, { month: 'short', day: 'numeric' })}</span>
        </div>
        {post.caption !== null && post.caption !== '' && <p className={styles.bestCaption}>{post.caption}</p>}
        <div className={styles.bestNumbers}>
          {numbers.map(({ key, message, count }) => (
            <span key={key}>{intl.formatMessage(message, { count })}</span>
          ))}
        </div>
      </div>
    </>
  );

  if (permalink === null) {
    return <div className={styles.best}>{body}</div>;
  }
  return (
    <a className={clsx(styles.best, styles.bestLink)} href={permalink} target="_blank" rel="noopener noreferrer">
      {body}
    </a>
  );
};

export const SocialBestPostCard = TypedMemo(SocialBestPostCardComponent);
